import { Button } from '@toggle/design-system';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useNavigateToThemeOverview } from '~/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useCatalystDrawer } from '~/widgets/catalyst-insights-drawer/useCatalystDrawer';

import { CatalystInsightWidget } from './catalyst-insight-widget/CatalystInsightWidget';
import * as S from './CatalystInsightDrawer.styles';

export const CatalystInsightDrawer = () => {
  const { t } = useTranslation(['insights']);
  const { navigateToPage } = useNavigateToAssetOverview();
  const { navigateToTheme } = useNavigateToThemeOverview();
  const { trackCatalystDrawerOpen } = usePosthogEvents();

  const {
    closeDrawer,
    isOpen,
    drawerType,
    entityTag,
    themeId,
    insightId,
    utmSource,
  } = useCatalystDrawer();

  useEffect(() => {
    if (isOpen && utmSource === POSTHOG_TRACKING_SOURCES.EMAIL) {
      trackCatalystDrawerOpen(utmSource, drawerType as 'company' | 'theme');
    }
  }, [isOpen, utmSource, drawerType]);

  const renderContent = () => {
    if (!insightId) {
      return null;
    }

    switch (drawerType) {
      case 'company':
        return entityTag ? (
          <CatalystInsightWidget
            entityTag={entityTag}
            insightId={insightId}
            type="company"
          />
        ) : null;
      case 'theme':
        return themeId ? (
          <CatalystInsightWidget
            themeId={themeId}
            insightId={insightId}
            type="theme"
          />
        ) : null;
      default:
        return null;
    }
  };

  const handleHeaderButtonClick = () => {
    if (drawerType === 'company' && entityTag) {
      navigateToPage({
        entityTag: entityTag,
      });
    }

    if (drawerType === 'theme' && themeId) {
      navigateToTheme({ themeId: themeId });
    }

    return null;
  };

  return (
    <S.StyledDrawer
      data-testid="catalyst-drawer"
      isOpen={isOpen}
      handleCloseClick={closeDrawer}
      headerButton={
        <Button
          variant="empty"
          label={
            drawerType === 'company'
              ? t('insights:catalyst.viewOnAO')
              : t('insights:catalyst.viewOnTheme')
          }
          iconName="Expand"
          size="xsmall"
          onClick={handleHeaderButtonClick}
        />
      }
    >
      <S.ContentWrapper>
        <ErrorBoundaryWrapper>{renderContent()}</ErrorBoundaryWrapper>
      </S.ContentWrapper>
    </S.StyledDrawer>
  );
};
